import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { Subject } from 'rxjs/internal/Subject';
import { take, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { DropzoneDirective, DropzoneModule } from 'ngx-dropzone-wrapper';
import { FormService } from '../../../common/services/form.service';
import { FormCommonDataService } from '../../../common/services/form-common-data.service';
import { ClientField } from '../../../common/models/client-field.model';
import { AttachmentService } from '@sb-tools/typescript/services/attachment.service';
import { Field } from '@sb-tools/typescript/models/field.model';
import { AttachmentBase } from '@sb-tools/typescript/models/attachment-base.model';
import { environment } from '@environment';
import { InlineSVGComponent } from '../../../../Frontend-Inline-SVG-Module/inline-svg.component';

@Component({
    selector: 'sb-attachment',
    templateUrl: './attachment.component.html',
    styleUrls: ['./attachment.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    imports: [InlineSVGComponent, DropzoneModule, FormsModule],
})
export class AttachmentComponent implements OnInit, OnDestroy {
    @ViewChild('dropzone') dropzone: DropzoneDirective;
    @Input() formField: Field | ClientField;

    config;
    files = [];

    private acceptedFiles?;
    private uploadMultiple?;
    private unsubscriber = new Subject<void>();

    constructor(
        public formCommonDataService: FormCommonDataService,
        public attachmentService: AttachmentService,
        public formService: FormService,
        private ngForm: NgForm,
    ) {}

    ngOnInit() {
        this.formField.value = [];
        this.config = this.attachmentService.getDropzoneConfig(
            environment.uploadsUrl + 'ticketAttachments/',
            25,
            this.uploadMultiple,
            this.acceptedFiles,
            0,
            '.dropzone-click-target',
        );

        this.formCommonDataService.attachmentsAddedSubject.pipe(takeUntil(this.unsubscriber)).subscribe((items) => {
            const processedItems: (AttachmentBase & { uniqueKey: string })[] = items.map((item) => ({
                ...item,
                uniqueKey: Math.random().toString(16).slice(2),
            }));

            this.files.push(...processedItems);
            this.formField.value.push(...processedItems);
            this.triggerFormValueChanges();

            const observables: Observable<Blob | MediaSource>[] = processedItems.map((item) => {
                return this.attachmentService.downloadFile(item.url);
            });

            combineLatest(observables)
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((result: (Blob | MediaSource)[]) => {
                    const dropzone = (this.dropzone as unknown as ElementRef).nativeElement.dropzone;
                    const urlCreator = window.URL || window.webkitURL;

                    const processedResult = result.map((item, index) => {
                        const file = item as Record<string, any>;
                        file.name = processedItems[index].name;
                        file.uniqueKey = processedItems[index].uniqueKey;
                        return file;
                    });

                    processedResult.forEach((file, index) => {
                        dropzone.displayExistingFile(file, urlCreator.createObjectURL(result[index]));
                    });
                });
        });
    }

    onKeyDown(event, focusedElement) {
        if (event.code === 'Enter' || event.code === 'Space') {
            event.preventDefault();
            focusedElement.click();
        }
    }

    onUploadSuccess(event) {
        this.files.push(event[1]);

        this.formField.value.push(event[1]);
        this.triggerFormValueChanges();
    }

    onProcessUploading() {
        this.attachmentService.changeUploadingStatus(false);
    }

    onFinishUploading() {
        this.attachmentService.changeUploadingStatus(true);
    }

    onRemovedFile(file) {
        let fileIndex;

        if (file.xhr) {
            const parsedFileUrl = JSON.parse(file.xhr.response).fileUrl;
            fileIndex = this.files.findIndex((item) => {
                return item.fileUrl === parsedFileUrl;
            });

            this.attachmentService.deleteFile(parsedFileUrl).pipe(take(1)).subscribe();
        } else {
            fileIndex = this.files.findIndex((item) => {
                return item.uniqueKey === file.uniqueKey;
            });
        }

        if (fileIndex > -1) {
            this.files.splice(fileIndex, 1);
            this.formField.value.splice(fileIndex, 1);
            this.triggerFormValueChanges();
        }
    }

    private triggerFormValueChanges(): void {
        this.ngForm.form.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
